import React from 'react'
import { makeRequest } from '../request'
import { Call, Device } from '@twilio/voice-sdk'
import { timeToCallString } from '../components/media/audioRecorder'
import { Button, ButtonShape } from '../components/simple/button'
import { IconMicrophone, IconMicrophoneSlash, IconPhone } from '../icons/library'

export type MakeCall = (
  to: { studentID: number; guardianID: number | undefined; name: string },
  onCallStarted: () => void
) => Promise<void>

interface callContext {
  makeCall: MakeCall
  inCall: boolean
}

const callContext = React.createContext<callContext>({} as callContext)

export const useCaller = () => {
  return React.useContext(callContext)
}

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [makingCall, setMakingCall] = React.useState(false)
  const [callTime, setCallTime] = React.useState<number | undefined>(undefined)
  const [recipientName, setRecipientName] = React.useState<string | undefined>(undefined)
  const [muted, setMuted] = React.useState(false)
  const lastCallRef = React.useRef<Call | null>(null)
  const deviceRef = React.useRef<Device | null>(null)

  const makeCall = React.useCallback(
    async (to: { studentID: number; guardianID: number | undefined; name: string }, onCallStarted: () => void) => {
      return new Promise<void>(async (resolve, rejectRaw) => {
        let timer: NodeJS.Timeout
        const done = () => {
          setMakingCall(false)
          resolve()
          clearInterval(timer)
        }
        const reject = (e: any) => {
          setMakingCall(false)
          clearInterval(timer)
          rejectRaw(e)
        }

        setMakingCall(true)
        setCallTime(undefined)
        setRecipientName(to.name)
        setMuted(true)

        const token = await makeRequest<{ token: string }>('/calltoken', { page: window.location.pathname }).then(
          (data) => data.token
        )
        deviceRef.current = new Device(token, { codecPreferences: [Call.Codec.Opus, Call.Codec.Opus] })
        deviceRef.current.on('error', reject)

        const connection = await deviceRef.current.connect({
          params: { student_id: to.studentID.toString(), parent_id: (to.guardianID || '').toString() },
        })
        connection.on('accept', () => {
          setCallTime(0)
          timer = setInterval(() => {
            setCallTime((t) => t! + 1)
          }, 1000)
          onCallStarted()
          lastCallRef.current = connection
        })
        connection.on('reject', done)
        connection.on('error', reject)
        connection.on('cancel', done)
        connection.on('disconnect', done)
        setMuted(false)
      })
    },
    []
  )

  React.useEffect(() => {
    if (lastCallRef.current) lastCallRef.current.mute(muted)
  }, [muted])

  return (
    <callContext.Provider value={{ makeCall, inCall: makingCall }}>
      {children}
      {makingCall && (
        <div className="pointer-events-auto fixed bottom-0 left-0 right-0 z-[10000] flex items-center justify-center gap-2 bg-white p-3 shadow">
          <div className="mr-4">In Call With: {recipientName}</div>
          <div className="mr-4">{callTime === undefined ? 'Ringing...' : timeToCallString(callTime)}</div>
          <div className="h-4 w-4 rounded-full bg-red-500" />
          Recording
          <Button
            className="ml-4"
            logo
            shape={ButtonShape.iconCircle}
            onClick={() => {
              setMuted((m) => !m)
            }}
            disabled={callTime === undefined}
          >
            {muted ? <IconMicrophoneSlash /> : <IconMicrophone />}
          </Button>
          <Button
            logo
            shape={ButtonShape.iconCircle}
            onClick={() => {
              if (deviceRef.current) deviceRef.current.disconnectAll()
            }}
          >
            <IconPhone />
          </Button>
        </div>
      )}
    </callContext.Provider>
  )
}
